"use client";

import Link from "next/link";

const PrivacyPolicy = () => {
  return (
    <main className="py-12 md:py-16">
      <div className="w-[90%] max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">
            Privacy Policy
          </h1>
          <p className="mt-4 text-sm opacity-70">
            Effective Date: January 2025 | Last Updated: January 2025
          </p>
        </div>

        {/* Main Content Card */}
        <div className="rounded-[18px] bg-gray-100 dark:bg-white/5 backdrop-blur-sm border border-gray-200 dark:border-white/10 p-6 md:p-10">
          {/* Intro */}
          <p className="leading-relaxed opacity-90">
            Lattu.ai is committed to protecting the privacy of its users. This
            Privacy Policy explains how we collect, use, disclose, and safeguard
            information when you access and use our platform, which provides
            AI-assisted planning and guidance for studying, traveling, working,
            and resettling across international borders.
          </p>

          {/* Section 1 */}
          <section className="mt-8">
            <h2 className="text-xl md:text-2xl font-bold">
              1. Information We Collect
            </h2>
            <p className="mt-3 opacity-90">
              We may collect the following categories of information:
            </p>

            <div className="mt-4 space-y-4">
              <div className="rounded-xl bg-white/60 dark:bg-white/5 border border-gray-200/80 dark:border-white/10 p-4">
                <h3 className="font-semibold text-cyan-600 dark:text-cyan-400">
                  a. Personal Information
                </h3>
                <p className="mt-2 text-sm opacity-80">
                  When you register or use personalized features, we may collect
                  information such as your name, email address, country of
                  residence, intended destination countries, educational
                  background, professional information, and planning preferences.
                </p>
              </div>

              <div className="rounded-xl bg-white/60 dark:bg-white/5 border border-gray-200/80 dark:border-white/10 p-4">
                <h3 className="font-semibold text-cyan-600 dark:text-cyan-400">
                  b. Usage and Technical Data
                </h3>
                <p className="mt-2 text-sm opacity-80">
                  We automatically collect information about your interaction
                  with the platform, including IP address, browser type, device
                  information, pages visited, features used, and timestamps.
                </p>
              </div>

              <div className="rounded-xl bg-white/60 dark:bg-white/5 border border-gray-200/80 dark:border-white/10 p-4">
                <h3 className="font-semibold text-cyan-600 dark:text-cyan-400">
                  c. Profile and Planning Data (Optional)
                </h3>
                <p className="mt-2 text-sm opacity-80">
                  To provide tailored recommendations, we may collect information
                  such as budget ranges, academic goals, language proficiency,
                  work experience, and visa or migration interests.
                </p>
              </div>
            </div>
          </section>

          {/* Section 2 */}
          <section className="mt-8">
            <h2 className="text-xl md:text-2xl font-bold">
              2. Use of Information
            </h2>
            <p className="mt-3 opacity-90">Your information is used to:</p>
            <ul className="mt-3 space-y-2 list-disc list-inside opacity-80">
              <li>
                Provide structured guidance, checklists, timelines, and cost
                estimates
              </li>
              <li>Personalize country comparisons and planning workflows</li>
              <li>
                Improve platform performance, content accuracy, and user
                experience
              </li>
              <li>
                Communicate updates, support responses, and service notifications
              </li>
              <li>
                Conduct internal analytics and research for service enhancement
              </li>
            </ul>
          </section>

          {/* Section 3 */}
          <section className="mt-8">
            <h2 className="text-xl md:text-2xl font-bold">3. AI Processing</h2>
            <p className="mt-3 opacity-90">
              Lattu.ai uses artificial intelligence systems to analyze user
              inputs and generate structured planning guidance. Your data may be
              processed by these systems solely for service delivery,
              personalization, and improvement purposes. We do not sell personal
              data to third parties.
            </p>
          </section>

          {/* Section 4 */}
          <section className="mt-8">
            <h2 className="text-xl md:text-2xl font-bold">4. Data Sharing</h2>
            <p className="mt-3 opacity-90">We may share information with:</p>
            <ul className="mt-3 space-y-2 list-disc list-inside opacity-80">
              <li>Cloud infrastructure and analytics providers</li>
              <li>Professional service providers (legal, security, compliance)</li>
              <li>Regulatory authorities when required by law</li>
              <li>Future expert or partner services, only with user consent</li>
            </ul>
          </section>

          {/* Section 5 */}
          <section className="mt-8">
            <h2 className="text-xl md:text-2xl font-bold">5. Data Security</h2>
            <p className="mt-3 opacity-90">
              We implement appropriate technical and organizational safeguards to
              protect personal data against unauthorized access, alteration,
              disclosure, or destruction.
            </p>
          </section>

          {/* Section 6 */}
          <section className="mt-8">
            <h2 className="text-xl md:text-2xl font-bold">
              6. Cookies and Tracking
            </h2>
            <p className="mt-3 opacity-90">
              We use cookies and similar technologies to enhance functionality,
              analyze usage, and remember user preferences. Users may manage
              cookie settings through their browser.
            </p>
          </section>

          {/* Section 7 */}
          <section className="mt-8">
            <h2 className="text-xl md:text-2xl font-bold">7. User Rights</h2>
            <p className="mt-3 opacity-90">
              Depending on jurisdiction, you may have the right to access,
              correct, delete, or restrict processing of your personal data, and
              to withdraw consent for optional features.
            </p>
          </section>

          {/* Section 8 */}
          <section className="mt-8">
            <h2 className="text-xl md:text-2xl font-bold">
              8. International Data Transfers
            </h2>
            <p className="mt-3 opacity-90">
              As a global platform, your information may be processed in
              countries outside your residence, in compliance with applicable
              data protection laws.
            </p>
          </section>

          {/* Section 9 */}
          <section className="mt-8">
            <h2 className="text-xl md:text-2xl font-bold">9. Policy Updates</h2>
            <p className="mt-3 opacity-90">
              We may update this Privacy Policy periodically. Continued use of
              the platform constitutes acceptance of the revised policy.
            </p>
          </section>

          {/* Contact */}
          <section className="mt-10 pt-6 border-t border-gray-200 dark:border-white/10">
            <p className="text-sm opacity-70 text-center">
              For questions about this Privacy Policy, please contact us through
              our{" "}
              <Link
                href="/#contact"
                className="text-cyan-600 dark:text-cyan-400 hover:underline"
              >
                Contact Form
              </Link>
              .
            </p>
          </section>
        </div>
      </div>
    </main>
  );
};

export default PrivacyPolicy;
